import { FC, useEffect, useMemo, useState, type ComponentProps, type ReactNode } from 'react';
import { Button, Calendar, Flex, Segmented, Space, Spin, Typography } from 'antd';
import { TbChevronLeft, TbChevronRight } from 'react-icons/tb';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { SmallTooltip } from '@/shared';
import { PlatformAlert } from '@/shared/components/alerts';
import type { SearchFilterJoin } from '@/shared/components/filters/searchFilters';
import { PlatformSelect as Select } from '@/shared/components/forms';
import { dayjs } from '@/utilities';

import CalendarEventKeyframe from './CalendarEventKeyframe';
import {
    CALENDAR_EVENT_SOURCE_IDS,
    CALENDAR_EVENT_SOURCES,
    type CalendarEventSourceId,
    type CalendarSelection,
    type EconomicEventImpact,
    type MarketCalendarEvent,
} from './calendarEvents';
import { matchesCalendarResearchFilters, type CalendarResearchFilters } from './calendarFilters';
import {
    CALENDAR_VIEW_MODES,
    getCalendarPeriod,
    getCalendarQueryPeriod,
    shiftCalendarPeriod,
    type CalendarDate,
    type CalendarViewMode,
} from './calendarPeriod';
import CalendarPeriodView from './CalendarPeriodView';
import CalendarSummaryCards from './CalendarSummaryCards';
import { useCalendarEvents } from './useCalendarEvents';

type AntCalendarProps = ComponentProps<typeof Calendar>;

interface MarketCalendarProps {
    researchFilters?: CalendarResearchFilters;
    sourcesDisabled?: boolean;
    onSourceFiltersChange?: (sources: CalendarEventSourceId[]) => void;
    filterJoin?: SearchFilterJoin;
    impactFilters?: readonly EconomicEventImpact[];
    onSelectEvents?: (selection?: CalendarSelection) => void;
    renderToolbar?: (toolbar: ReactNode) => ReactNode;
    sourceFilters?: readonly CalendarEventSourceId[];
}

const calendarEventLabel = (event: MarketCalendarEvent): string =>
    event.source === CALENDAR_EVENT_SOURCE_IDS.dividend ? event.symbol : event.title;

const calendarViewOptions = CALENDAR_VIEW_MODES.map((view) => ({
    label: `${view.charAt(0).toLocaleUpperCase()}${view.slice(1)}`,
    value: view,
}));

const isCalendarViewMode = (value: unknown): value is CalendarViewMode =>
    typeof value === 'string' && CALENDAR_VIEW_MODES.includes(value as CalendarViewMode);

const MarketCalendar: FC<MarketCalendarProps> = ({
    researchFilters = {},
    sourcesDisabled = false,
    onSourceFiltersChange,
    impactFilters = [],
    onSelectEvents,
    renderToolbar,
    sourceFilters = [],
}): JSX.Element => {
    const { token } = useAntDesignTheme();
    const [calendarValue, setCalendarValue] = useState<CalendarDate>(() => dayjs());
    const [calendarView, setCalendarView] = useState<CalendarViewMode>('month');

    const visiblePeriod = useMemo(() => getCalendarPeriod(calendarValue, calendarView), [calendarValue, calendarView]);
    const dateRange = useMemo(() => getCalendarQueryPeriod(calendarValue, calendarView), [calendarValue, calendarView]);
    const { events, failedSources, loading } = useCalendarEvents(dateRange);
    const selectedSources = sourcesDisabled
        ? []
        : sourceFilters.length
          ? sourceFilters
          : researchFilters.coverage === 'all'
            ? ['dividend', 'economic']
            : ['economic'];
    const visibleEvents = useMemo(
        () =>
            events.filter((event) => {
                if (sourcesDisabled || (sourceFilters.length && !sourceFilters.includes(event.source))) {
                    return false;
                }
                if (
                    !matchesCalendarResearchFilters(
                        event,
                        researchFilters,
                        impactFilters.length > 0,
                        sourceFilters.length > 0,
                    )
                ) {
                    return false;
                }
                return !impactFilters.length || event.source !== 'economic' || impactFilters.includes(event.impact);
            }),
        [events, impactFilters, sourceFilters, researchFilters, sourcesDisabled],
    );

    const eventsByDate = useMemo(
        () =>
            visibleEvents.reduce<Record<string, MarketCalendarEvent[]>>((groupedEvents, event) => {
                groupedEvents[event.date] = [...(groupedEvents[event.date] ?? []), event];
                return groupedEvents;
            }, {}),
        [visibleEvents],
    );

    useEffect(() => {
        const date = calendarValue.format('YYYY-MM-DD');
        onSelectEvents?.({ date, events: eventsByDate[date] ?? [] });
    }, [calendarValue, eventsByDate, onSelectEvents]);

    const failedSourceLabels = failedSources
        .map((sourceId) => CALENDAR_EVENT_SOURCES.find((source) => source.id === sourceId)?.label)
        .filter((label): label is string => Boolean(label));

    const cellRender: NonNullable<AntCalendarProps['cellRender']> = (value, info) => {
        if (info.type !== 'date') {
            return info.originNode;
        }

        const dateEvents = eventsByDate[value.format('YYYY-MM-DD')] ?? [];
        const visibleEvents = dateEvents.slice(0, 3);
        const hiddenEventCount = dateEvents.length - visibleEvents.length;

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', paddingTop: '0.25rem' }}>
                {visibleEvents.map((event) => (
                    <SmallTooltip key={event.id} title={event.title}>
                        <Flex
                            align='center'
                            gap={5}
                            style={{
                                backgroundColor: token.colorFillQuaternary,
                                borderRadius: token.borderRadiusSM,
                                minWidth: 0,
                                padding: '0.125rem 0.25rem',
                            }}
                        >
                            <CalendarEventKeyframe size='0.75rem' source={event.source} />
                            <Typography.Text ellipsis style={{ fontSize: token.fontSizeSM, lineHeight: 1.25 }}>
                                {calendarEventLabel(event)}
                            </Typography.Text>
                        </Flex>
                    </SmallTooltip>
                ))}
                {hiddenEventCount > 0 && (
                    <Typography.Text type='secondary' style={{ fontSize: token.fontSizeSM }}>
                        +{hiddenEventCount} more
                    </Typography.Text>
                )}
            </div>
        );
    };

    const handleSelectDate = (date: CalendarDate) => {
        const normalizedDate = dayjs(date.format('YYYY-MM-DD'));
        const dateKey = normalizedDate.format('YYYY-MM-DD');
        const selectedEvents = eventsByDate[dateKey] ?? [];

        setCalendarValue(normalizedDate);
        onSelectEvents?.({ date: dateKey, events: selectedEvents });
    };

    const calendarToolbar = (() => {
        const monthOptions = Array.from({ length: 12 }, (_, monthIndex) => (
            <Select.Option key={monthIndex} value={monthIndex}>
                {calendarValue.month(monthIndex).format('MMM')}
            </Select.Option>
        ));
        const quarterOptions = Array.from({ length: 4 }, (_, index) => (
            <Select.Option key={index + 1} value={index + 1}>
                Q{index + 1}
            </Select.Option>
        ));
        const year = calendarValue.year();
        const yearOptions = Array.from({ length: 20 }, (_, index) => year - 10 + index).map((optionYear) => (
            <Select.Option key={optionYear} value={optionYear}>
                {optionYear}
            </Select.Option>
        ));

        return (
            <Flex align='center' gap={8} justify='space-between' style={{ minWidth: 0, width: '100%' }} wrap>
                <Space.Compact>
                    <Button size='small' type='dashed' onClick={() => setCalendarValue(dayjs())}>
                        Today
                    </Button>
                    <SmallTooltip placement='bottom' title={`Previous ${calendarView}`}>
                        <Button
                            aria-label={`Previous ${calendarView}`}
                            icon={<TbChevronLeft size='1rem' />}
                            size='small'
                            onClick={() => setCalendarValue(shiftCalendarPeriod(calendarValue, calendarView, -1))}
                        />
                    </SmallTooltip>
                    <SmallTooltip placement='bottom' title={`Next ${calendarView}`}>
                        <Button
                            aria-label={`Next ${calendarView}`}
                            icon={<TbChevronRight size='1rem' />}
                            size='small'
                            onClick={() => setCalendarValue(shiftCalendarPeriod(calendarValue, calendarView, 1))}
                        />
                    </SmallTooltip>
                </Space.Compact>

                <Space size={12} wrap>
                    {CALENDAR_EVENT_SOURCES.map((source) => (
                        <Button
                            key={source.id}
                            aria-pressed={selectedSources.includes(source.id)}
                            aria-label={`Toggle ${source.label}`}
                            icon={<CalendarEventKeyframe size='0.875rem' source={source.id} />}
                            size='small'
                            type={selectedSources.includes(source.id) ? 'default' : 'text'}
                            style={{ opacity: selectedSources.includes(source.id) ? 1 : 0.5 }}
                            onClick={() =>
                                onSourceFiltersChange?.(
                                    CALENDAR_EVENT_SOURCES.filter((candidate) =>
                                        candidate.id === source.id
                                            ? !selectedSources.includes(candidate.id)
                                            : selectedSources.includes(candidate.id),
                                    ).map((candidate) => candidate.id),
                                )
                            }
                        >
                            {source.label}
                        </Button>
                    ))}
                </Space>

                <Space size={8} wrap>
                    <Segmented
                        options={calendarViewOptions}
                        size='small'
                        value={calendarView}
                        onChange={(nextView) => {
                            if (isCalendarViewMode(nextView)) {
                                setCalendarView(nextView);
                            }
                        }}
                    />
                    <Space.Compact>
                        {calendarView === 'quarter' ? (
                            <Select
                                dropdownMatchSelectWidth={false}
                                size='small'
                                value={calendarValue.quarter()}
                                onChange={(newQuarter: unknown) => {
                                    if (typeof newQuarter === 'number') {
                                        setCalendarValue(calendarValue.quarter(newQuarter));
                                    }
                                }}
                            >
                                {quarterOptions}
                            </Select>
                        ) : (
                            <Select
                                dropdownMatchSelectWidth={false}
                                size='small'
                                value={calendarValue.month()}
                                onChange={(newMonth: unknown) => {
                                    if (typeof newMonth === 'number') {
                                        setCalendarValue(calendarValue.month(newMonth));
                                    }
                                }}
                            >
                                {monthOptions}
                            </Select>
                        )}
                        <Select
                            dropdownMatchSelectWidth={false}
                            size='small'
                            value={year}
                            onChange={(newYear: unknown) => {
                                if (typeof newYear === 'number') {
                                    setCalendarValue(calendarValue.year(newYear));
                                }
                            }}
                        >
                            {yearOptions}
                        </Select>
                    </Space.Compact>
                </Space>
            </Flex>
        );
    })();

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, overflow: 'hidden' }}>
            {renderToolbar?.(calendarToolbar)}
            {failedSourceLabels.length > 0 && (
                <PlatformAlert
                    banner
                    closable
                    title='Some calendar sources are unavailable'
                    description={`${failedSourceLabels.join(' and ')} could not be loaded. Other calendar events remain available.`}
                    showIcon
                    type='warning'
                />
            )}
            <div style={{ flex: 'none', padding: '0.25rem 0 0.75rem' }}>
                <CalendarSummaryCards
                    events={visibleEvents}
                    from={visiblePeriod.from}
                    loading={loading}
                    periodLabel={visiblePeriod.label}
                    to={visiblePeriod.to}
                />
            </div>
            <div style={{ flex: '1 1 0%', minHeight: 0, overflow: 'auto' }}>
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        minHeight: '100%',
                        width: '100%',
                    }}
                >
                    {!renderToolbar ? <div style={{ padding: '0.5rem 0' }}>{calendarToolbar}</div> : null}
                    <Spin spinning={loading} style={{ width: '100%' }}>
                        {calendarView === 'month' ? (
                            <Calendar
                                cellRender={cellRender}
                                headerRender={() => null}
                                onChange={(date) => setCalendarValue(dayjs(date.format('YYYY-MM-DD')))}
                                onPanelChange={(date) => setCalendarValue(dayjs(date.format('YYYY-MM-DD')))}
                                onSelect={(date, selectInfo) => {
                                    if (selectInfo.source === 'date') {
                                        handleSelectDate(dayjs(date.format('YYYY-MM-DD')));
                                    }
                                }}
                                style={{ width: '100%' }}
                                value={calendarValue}
                            />
                        ) : (
                            <CalendarPeriodView
                                eventsByDate={eventsByDate}
                                onSelectDate={handleSelectDate}
                                value={calendarValue}
                                view={calendarView}
                            />
                        )}
                    </Spin>
                </div>
            </div>
        </div>
    );
};

export default MarketCalendar;
